import styles from './ScoopCircularProgress.module.scss'

/**
 * ScoopCircularProgress - A circular progress indicator
 *
 * @component
 * @param {Object} props - Component props
 * @param {'determinate' | 'indeterminate'} [props.variant='indeterminate'] - Progress variant
 * @param {number} [props.value=0] - Progress value (0-100) for determinate variant
 * @param {number} [props.size=24] - Size in pixels
 * @param {number} [props.thickness=3] - Stroke thickness
 * @param {string} [props.className] - Additional CSS classes
 * @param {Object} [props.style] - Inline styles
 *
 * @example
 * // Indeterminate (spinning)
 * <ScoopCircularProgress />
 *
 * @example
 * // Determinate with value
 * <ScoopCircularProgress variant="determinate" value={75} />
 */
export const ScoopCircularProgress = ({
    variant = 'indeterminate',
    value = 0,
    size = 24,
    thickness = 3,
    className = '',
    style,
    ...props
}) => {
    const radius = (size - thickness) / 2
    const circumference = 2 * Math.PI * radius
    const normalizedValue = Math.min(100, Math.max(0, value))
    const strokeDashoffset = circumference - (normalizedValue / 100) * circumference

    const containerClassName = [
        styles.container,
        variant === 'indeterminate' && styles.indeterminate,
        className,
    ]
        .filter(Boolean)
        .join(' ')

    return (
        <svg
            className={containerClassName}
            width={size}
            height={size}
            viewBox={`0 0 ${size} ${size}`}
            style={style}
            {...props}
        >
            {/* Background circle */}
            <circle
                className={styles.background}
                cx={size / 2}
                cy={size / 2}
                r={radius}
                strokeWidth={thickness}
                fill="none"
            />
            {/* Progress circle */}
            <circle
                className={styles.progress}
                cx={size / 2}
                cy={size / 2}
                r={radius}
                strokeWidth={thickness}
                fill="none"
                strokeDasharray={circumference}
                strokeDashoffset={
                    variant === 'determinate' ? strokeDashoffset : circumference * 0.75
                }
                strokeLinecap="round"
            />
        </svg>
    )
}

export default ScoopCircularProgress
